/**
 * Email validation utility for checking if an email uses a work domain.
 * This provides client-side validation for common free email providers.
 * The backend has a comprehensive list and will also validate.
 */

// Exception emails that bypass the free email block (for testing purposes)
const ALLOWED_FREE_EMAILS = new Set([
  'aimdoc.info@gmail.com',
]);

// Common free email providers for quick client-side validation
const FREE_EMAIL_DOMAINS = new Set([
  // Major providers
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.es',
  'yahoo.it',
  'yahoo.ca',
  'yahoo.com.au',
  'yahoo.co.in',
  'outlook.com',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  
  // Privacy-focused
  'protonmail.com',
  'protonmail.ch',
  'proton.me',
  'tutanota.com',
  'tutanota.de',
  'tutamail.com',
  'tuta.io',
  
  // Temporary/disposable
  'tempmail.com',
  'guerrillamail.com',
  '10minutemail.com',
  'mailinator.com',
  'throwaway.email',
  'temp-mail.org',
  
  // Other popular free providers
  'mail.com',
  'yandex.com',
  'yandex.ru',
  'zoho.com',
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'mail.ru',
  'inbox.com',
  'fastmail.com',
  'qq.com',
  '163.com',
  '126.com',
  'rediffmail.com',
]);

/**
 * Check if an email address uses a work domain (not a free/disposable provider).
 * 
 * @param email - Email address to validate
 * @returns true if the email appears to use a work domain, false if it uses a known free provider
 */
export function isWorkEmail(email: string): boolean {
  if (!email || !email.includes('@')) {
    return false;
  }

  try {
    // Normalize email for comparison
    const normalizedEmail = email.toLowerCase().trim();
    
    // Check if email is in the allowed exceptions list (for testing purposes)
    if (ALLOWED_FREE_EMAILS.has(normalizedEmail)) {
      return true;
    }
    
    // Extract domain from email
    const domain = email.split('@')[1].toLowerCase().trim();
    
    // Check if domain is in the free email providers list
    return !FREE_EMAIL_DOMAINS.has(domain);
  } catch (error) {
    console.error('Error validating email:', error);
    // On error, return true to allow backend validation
    return true;
  }
}

/**
 * Get the domain from an email address.
 * 
 * @param email - Email address
 * @returns domain name or null if invalid
 */
export function getEmailDomain(email: string): string | null {
  if (!email || !email.includes('@')) {
    return null;
  }

  try {
    return email.split('@')[1].toLowerCase().trim();
  } catch (error) {
    return null;
  }
}

/**
 * Get a user-friendly error message for work email validation.
 * 
 * @returns Error message string
 */
export function getWorkEmailErrorMessage(): string {
  return 'Please use a work email address to sign up. Free email providers are not allowed.';
}

